/**
 * AnimationManager.ts - CSS animation and transition management
 * 
 * CHANGE HISTORY:
 * - 251102_1715 danielbeer: Created for Phase 3.5b - Advanced Features
 * 
 * Provides:
 * - CSS transition definitions
 * - Keyframe animation creation
 * - Animation presets (fade, slide, scale, rotate, bounce)
 * - Timing function library
 * - Animation composition
 */

import type { StyleVariables } from '../types';

export interface Transition {
  property: string;
  duration: string;
  timingFunction: string;
  delay?: string;
}

export interface Keyframe {
  offset: number; // 0-100
  properties: Partial<StyleVariables>;
}

export interface Animation {
  name: string;
  keyframes: Keyframe[];
  duration: string;
  timingFunction: string;
  delay?: string;
  iterationCount?: string | number;
  direction?: 'normal' | 'reverse' | 'alternate' | 'alternate-reverse';
  fillMode?: 'none' | 'forwards' | 'backwards' | 'both';
}

export interface AnimationPreset {
  name: string;
  description: string;
  animation: Animation;
}

/**
 * Timing functions
 */
export const TIMING_FUNCTIONS = {
  // Standard
  linear: 'linear',
  ease: 'ease',
  easeIn: 'ease-in',
  easeOut: 'ease-out',
  easeInOut: 'ease-in-out',
  
  // Cubic bezier presets
  easeInSine: 'cubic-bezier(0.12, 0, 0.39, 0)',
  easeOutSine: 'cubic-bezier(0.61, 1, 0.88, 1)',
  easeInOutSine: 'cubic-bezier(0.37, 0, 0.63, 1)',
  
  easeInQuad: 'cubic-bezier(0.11, 0, 0.5, 0)',
  easeOutQuad: 'cubic-bezier(0.5, 1, 0.89, 1)',
  easeInOutQuad: 'cubic-bezier(0.45, 0, 0.55, 1)',
  
  easeInCubic: 'cubic-bezier(0.32, 0, 0.67, 0)',
  easeOutCubic: 'cubic-bezier(0.33, 1, 0.68, 1)',
  easeInOutCubic: 'cubic-bezier(0.65, 0, 0.35, 1)',
  
  easeInQuart: 'cubic-bezier(0.5, 0, 0.75, 0)',
  easeOutQuart: 'cubic-bezier(0.25, 1, 0.5, 1)',
  easeInOutQuart: 'cubic-bezier(0.76, 0, 0.24, 1)',
  
  easeInQuint: 'cubic-bezier(0.64, 0, 0.78, 0)',
  easeOutQuint: 'cubic-bezier(0.22, 1, 0.36, 1)',
  easeInOutQuint: 'cubic-bezier(0.83, 0, 0.17, 1)',
  
  easeInExpo: 'cubic-bezier(0.7, 0, 0.84, 0)',
  easeOutExpo: 'cubic-bezier(0.16, 1, 0.3, 1)',
  easeInOutExpo: 'cubic-bezier(0.87, 0, 0.13, 1)',
  
  easeInCirc: 'cubic-bezier(0.55, 0, 1, 0.45)',
  easeOutCirc: 'cubic-bezier(0, 0.55, 0.45, 1)',
  easeInOutCirc: 'cubic-bezier(0.85, 0, 0.15, 1)',
  
  easeInBack: 'cubic-bezier(0.36, 0, 0.66, -0.56)',
  easeOutBack: 'cubic-bezier(0.34, 1.56, 0.64, 1)',
  easeInOutBack: 'cubic-bezier(0.68, -0.6, 0.32, 1.6)',
  
  // Spring-like
  spring: 'cubic-bezier(0.68, -0.55, 0.265, 1.55)',
  bounce: 'cubic-bezier(0.68, -0.55, 0.265, 1.55)'
};

/**
 * Animation presets
 */
export const ANIMATION_PRESETS: Record<string, AnimationPreset> = {
  fadeIn: {
    name: 'Fade In',
    description: 'Fade element in from transparent to opaque',
    animation: {
      name: 'fadeIn',
      keyframes: [
        { offset: 0, properties: { '--opacity': '0' } },
        { offset: 100, properties: { '--opacity': '1' } }
      ],
      duration: '300ms',
      timingFunction: TIMING_FUNCTIONS.easeOut,
      fillMode: 'both'
    }
  },
  
  fadeOut: {
    name: 'Fade Out',
    description: 'Fade element out from opaque to transparent',
    animation: {
      name: 'fadeOut',
      keyframes: [
        { offset: 0, properties: { '--opacity': '1' } },
        { offset: 100, properties: { '--opacity': '0' } }
      ],
      duration: '300ms',
      timingFunction: TIMING_FUNCTIONS.easeIn,
      fillMode: 'both'
    }
  },
  
  slideInLeft: {
    name: 'Slide In Left',
    description: 'Slide element in from the left',
    animation: {
      name: 'slideInLeft',
      keyframes: [
        { offset: 0, properties: { '--translate-x': '-100%', '--opacity': '0' } },
        { offset: 100, properties: { '--translate-x': '0', '--opacity': '1' } }
      ],
      duration: '400ms',
      timingFunction: TIMING_FUNCTIONS.easeOutCubic,
      fillMode: 'both'
    }
  },
  
  slideInRight: {
    name: 'Slide In Right',
    description: 'Slide element in from the right',
    animation: {
      name: 'slideInRight',
      keyframes: [
        { offset: 0, properties: { '--translate-x': '100%', '--opacity': '0' } },
        { offset: 100, properties: { '--translate-x': '0', '--opacity': '1' } }
      ],
      duration: '400ms',
      timingFunction: TIMING_FUNCTIONS.easeOutCubic,
      fillMode: 'both'
    }
  },
  
  slideInUp: {
    name: 'Slide In Up',
    description: 'Slide element in from below',
    animation: {
      name: 'slideInUp',
      keyframes: [
        { offset: 0, properties: { '--translate-y': '100%', '--opacity': '0' } },
        { offset: 100, properties: { '--translate-y': '0', '--opacity': '1' } }
      ],
      duration: '400ms',
      timingFunction: TIMING_FUNCTIONS.easeOutCubic,
      fillMode: 'both'
    }
  },
  
  slideInDown: {
    name: 'Slide In Down',
    description: 'Slide element in from above',
    animation: {
      name: 'slideInDown',
      keyframes: [
        { offset: 0, properties: { '--translate-y': '-100%', '--opacity': '0' } },
        { offset: 100, properties: { '--translate-y': '0', '--opacity': '1' } }
      ],
      duration: '400ms',
      timingFunction: TIMING_FUNCTIONS.easeOutCubic,
      fillMode: 'both'
    }
  },
  
  scaleIn: {
    name: 'Scale In',
    description: 'Scale element in from small to normal',
    animation: {
      name: 'scaleIn',
      keyframes: [
        { offset: 0, properties: { '--scale': '0', '--opacity': '0' } },
        { offset: 100, properties: { '--scale': '1', '--opacity': '1' } }
      ],
      duration: '300ms',
      timingFunction: TIMING_FUNCTIONS.easeOutBack,
      fillMode: 'both'
    }
  },
  
  scaleOut: {
    name: 'Scale Out',
    description: 'Scale element out from normal to small',
    animation: {
      name: 'scaleOut',
      keyframes: [
        { offset: 0, properties: { '--scale': '1', '--opacity': '1' } },
        { offset: 100, properties: { '--scale': '0', '--opacity': '0' } }
      ],
      duration: '300ms',
      timingFunction: TIMING_FUNCTIONS.easeInBack,
      fillMode: 'both'
    }
  },
  
  rotate: {
    name: 'Rotate',
    description: 'Rotate element 360 degrees',
    animation: {
      name: 'rotate',
      keyframes: [
        { offset: 0, properties: { '--rotate': '0deg' } },
        { offset: 100, properties: { '--rotate': '360deg' } }
      ],
      duration: '1000ms',
      timingFunction: TIMING_FUNCTIONS.linear,
      iterationCount: 'infinite'
    }
  },
  
  pulse: {
    name: 'Pulse',
    description: 'Pulsing scale animation',
    animation: {
      name: 'pulse',
      keyframes: [
        { offset: 0, properties: { '--scale': '1' } },
        { offset: 50, properties: { '--scale': '1.05' } },
        { offset: 100, properties: { '--scale': '1' } }
      ],
      duration: '1000ms',
      timingFunction: TIMING_FUNCTIONS.easeInOut,
      iterationCount: 'infinite'
    }
  },
  
  bounce: {
    name: 'Bounce',
    description: 'Bouncing animation',
    animation: {
      name: 'bounce',
      keyframes: [
        { offset: 0, properties: { '--translate-y': '0' } },
        { offset: 25, properties: { '--translate-y': '-30px' } },
        { offset: 50, properties: { '--translate-y': '0' } },
        { offset: 75, properties: { '--translate-y': '-15px' } },
        { offset: 100, properties: { '--translate-y': '0' } }
      ],
      duration: '1000ms',
      timingFunction: TIMING_FUNCTIONS.easeInOut,
      iterationCount: 'infinite'
    }
  },
  
  shake: {
    name: 'Shake',
    description: 'Horizontal shake animation',
    animation: {
      name: 'shake',
      keyframes: [
        { offset: 0, properties: { '--translate-x': '0' } },
        { offset: 10, properties: { '--translate-x': '-10px' } },
        { offset: 20, properties: { '--translate-x': '10px' } },
        { offset: 30, properties: { '--translate-x': '-10px' } },
        { offset: 40, properties: { '--translate-x': '10px' } },
        { offset: 50, properties: { '--translate-x': '-10px' } },
        { offset: 60, properties: { '--translate-x': '10px' } },
        { offset: 70, properties: { '--translate-x': '-10px' } },
        { offset: 80, properties: { '--translate-x': '10px' } },
        { offset: 90, properties: { '--translate-x': '-10px' } },
        { offset: 100, properties: { '--translate-x': '0' } }
      ],
      duration: '500ms',
      timingFunction: TIMING_FUNCTIONS.linear
    }
  },
  
  wobble: {
    name: 'Wobble',
    description: 'Wobble rotation animation',
    animation: {
      name: 'wobble',
      keyframes: [
        { offset: 0, properties: { '--rotate': '0deg' } },
        { offset: 15, properties: { '--rotate': '-5deg' } },
        { offset: 30, properties: { '--rotate': '3deg' } },
        { offset: 45, properties: { '--rotate': '-3deg' } },
        { offset: 60, properties: { '--rotate': '2deg' } },
        { offset: 75, properties: { '--rotate': '-1deg' } },
        { offset: 100, properties: { '--rotate': '0deg' } }
      ],
      duration: '500ms',
      timingFunction: TIMING_FUNCTIONS.easeInOut
    }
  },
  
  // Attention Seekers
  flash: {
    name: 'Flash',
    description: 'Flash opacity animation',
    animation: {
      name: 'flash',
      keyframes: [
        { offset: 0, properties: { '--opacity': '1' } },
        { offset: 25, properties: { '--opacity': '0' } },
        { offset: 50, properties: { '--opacity': '1' } },
        { offset: 75, properties: { '--opacity': '0' } },
        { offset: 100, properties: { '--opacity': '1' } }
      ],
      duration: '1000ms',
      timingFunction: TIMING_FUNCTIONS.linear
    }
  },
  
  headShake: {
    name: 'Head Shake',
    description: 'Horizontal head shake',
    animation: {
      name: 'headShake',
      keyframes: [
        { offset: 0, properties: { '--translate-x': '0' } },
        { offset: 6.5, properties: { '--translate-x': '-6px', '--rotate': '-9deg' } },
        { offset: 18.5, properties: { '--translate-x': '5px', '--rotate': '7deg' } },
        { offset: 31.5, properties: { '--translate-x': '-3px', '--rotate': '-5deg' } },
        { offset: 43.5, properties: { '--translate-x': '2px', '--rotate': '3deg' } },
        { offset: 50, properties: { '--translate-x': '0', '--rotate': '0deg' } }
      ],
      duration: '1000ms',
      timingFunction: TIMING_FUNCTIONS.easeInOut
    }
  },
  
  heartBeat: {
    name: 'Heart Beat',
    description: 'Pulsing heartbeat animation',
    animation: {
      name: 'heartBeat',
      keyframes: [
        { offset: 0, properties: { '--scale': '1' } },
        { offset: 14, properties: { '--scale': '1.3' } },
        { offset: 28, properties: { '--scale': '1' } },
        { offset: 42, properties: { '--scale': '1.3' } },
        { offset: 70, properties: { '--scale': '1' } }
      ],
      duration: '1300ms',
      timingFunction: TIMING_FUNCTIONS.easeInOut,
      iterationCount: 'infinite'
    }
  },
  
  jello: {
    name: 'Jello',
    description: 'Jello wobble effect',
    animation: {
      name: 'jello',
      keyframes: [
        { offset: 0, properties: { '--skew-x': '0deg', '--skew-y': '0deg' } },
        { offset: 11.1, properties: { '--skew-x': '0deg', '--skew-y': '0deg' } },
        { offset: 22.2, properties: { '--skew-x': '-12.5deg', '--skew-y': '-12.5deg' } },
        { offset: 33.3, properties: { '--skew-x': '6.25deg', '--skew-y': '6.25deg' } },
        { offset: 44.4, properties: { '--skew-x': '-3.125deg', '--skew-y': '-3.125deg' } },
        { offset: 55.5, properties: { '--skew-x': '1.5625deg', '--skew-y': '1.5625deg' } },
        { offset: 66.6, properties: { '--skew-x': '-0.78125deg', '--skew-y': '-0.78125deg' } },
        { offset: 77.7, properties: { '--skew-x': '0.390625deg', '--skew-y': '0.390625deg' } },
        { offset: 88.8, properties: { '--skew-x': '-0.1953125deg', '--skew-y': '-0.1953125deg' } },
        { offset: 100, properties: { '--skew-x': '0deg', '--skew-y': '0deg' } }
      ],
      duration: '900ms',
      timingFunction: TIMING_FUNCTIONS.linear
    }
  },
  
  rubberBand: {
    name: 'Rubber Band',
    description: 'Elastic rubber band stretch',
    animation: {
      name: 'rubberBand',
      keyframes: [
        { offset: 0, properties: { '--scale-x': '1', '--scale-y': '1' } },
        { offset: 30, properties: { '--scale-x': '1.25', '--scale-y': '0.75' } },
        { offset: 40, properties: { '--scale-x': '0.75', '--scale-y': '1.25' } },
        { offset: 50, properties: { '--scale-x': '1.15', '--scale-y': '0.85' } },
        { offset: 65, properties: { '--scale-x': '0.95', '--scale-y': '1.05' } },
        { offset: 75, properties: { '--scale-x': '1.05', '--scale-y': '0.95' } },
        { offset: 100, properties: { '--scale-x': '1', '--scale-y': '1' } }
      ],
      duration: '1000ms',
      timingFunction: TIMING_FUNCTIONS.linear
    }
  },
  
  swing: {
    name: 'Swing',
    description: 'Pendulum swing animation',
    animation: {
      name: 'swing',
      keyframes: [
        { offset: 0, properties: { '--rotate': '0deg' } },
        { offset: 20, properties: { '--rotate': '15deg' } },
        { offset: 40, properties: { '--rotate': '-10deg' } },
        { offset: 60, properties: { '--rotate': '5deg' } },
        { offset: 80, properties: { '--rotate': '-5deg' } },
        { offset: 100, properties: { '--rotate': '0deg' } }
      ],
      duration: '1000ms',
      timingFunction: TIMING_FUNCTIONS.linear
    }
  },
  
  tada: {
    name: 'Tada',
    description: 'Celebratory tada animation',
    animation: {
      name: 'tada',
      keyframes: [
        { offset: 0, properties: { '--scale': '1', '--rotate': '0deg' } },
        { offset: 10, properties: { '--scale': '0.9', '--rotate': '-3deg' } },
        { offset: 20, properties: { '--scale': '0.9', '--rotate': '-3deg' } },
        { offset: 30, properties: { '--scale': '1.1', '--rotate': '3deg' } },
        { offset: 40, properties: { '--scale': '1.1', '--rotate': '-3deg' } },
        { offset: 50, properties: { '--scale': '1.1', '--rotate': '3deg' } },
        { offset: 60, properties: { '--scale': '1.1', '--rotate': '-3deg' } },
        { offset: 70, properties: { '--scale': '1.1', '--rotate': '3deg' } },
        { offset: 80, properties: { '--scale': '1.1', '--rotate': '-3deg' } },
        { offset: 90, properties: { '--scale': '1.1', '--rotate': '3deg' } },
        { offset: 100, properties: { '--scale': '1', '--rotate': '0deg' } }
      ],
      duration: '1000ms',
      timingFunction: TIMING_FUNCTIONS.linear
    }
  },
  
  // Entrances
  bounceIn: {
    name: 'Bounce In',
    description: 'Bounce entrance animation',
    animation: {
      name: 'bounceIn',
      keyframes: [
        { offset: 0, properties: { '--scale': '0.3', '--opacity': '0' } },
        { offset: 20, properties: { '--scale': '1.1', '--opacity': '1' } },
        { offset: 40, properties: { '--scale': '0.9' } },
        { offset: 60, properties: { '--scale': '1.03' } },
        { offset: 80, properties: { '--scale': '0.97' } },
        { offset: 100, properties: { '--scale': '1', '--opacity': '1' } }
      ],
      duration: '750ms',
      timingFunction: TIMING_FUNCTIONS.easeOutCubic,
      fillMode: 'both'
    }
  },
  
  flipIn: {
    name: 'Flip In',
    description: 'Flip entrance animation',
    animation: {
      name: 'flipIn',
      keyframes: [
        { offset: 0, properties: { '--rotate-y': '90deg', '--opacity': '0' } },
        { offset: 40, properties: { '--rotate-y': '-20deg' } },
        { offset: 60, properties: { '--rotate-y': '10deg', '--opacity': '1' } },
        { offset: 80, properties: { '--rotate-y': '-5deg' } },
        { offset: 100, properties: { '--rotate-y': '0deg', '--opacity': '1' } }
      ],
      duration: '600ms',
      timingFunction: TIMING_FUNCTIONS.easeOut,
      fillMode: 'both'
    }
  },
  
  zoomIn: {
    name: 'Zoom In',
    description: 'Zoom entrance animation',
    animation: {
      name: 'zoomIn',
      keyframes: [
        { offset: 0, properties: { '--scale': '0', '--opacity': '0' } },
        { offset: 50, properties: { '--opacity': '1' } },
        { offset: 100, properties: { '--scale': '1', '--opacity': '1' } }
      ],
      duration: '500ms',
      timingFunction: TIMING_FUNCTIONS.easeOut,
      fillMode: 'both'
    }
  },
  
  // Exits
  bounceOut: {
    name: 'Bounce Out',
    description: 'Bounce exit animation',
    animation: {
      name: 'bounceOut',
      keyframes: [
        { offset: 0, properties: { '--scale': '1', '--opacity': '1' } },
        { offset: 20, properties: { '--scale': '0.9' } },
        { offset: 50, properties: { '--scale': '1.1', '--opacity': '1' } },
        { offset: 100, properties: { '--scale': '0.3', '--opacity': '0' } }
      ],
      duration: '750ms',
      timingFunction: TIMING_FUNCTIONS.easeInCubic,
      fillMode: 'both'
    }
  },
  
  flipOut: {
    name: 'Flip Out',
    description: 'Flip exit animation',
    animation: {
      name: 'flipOut',
      keyframes: [
        { offset: 0, properties: { '--rotate-y': '0deg', '--opacity': '1' } },
        { offset: 30, properties: { '--rotate-y': '-15deg', '--opacity': '1' } },
        { offset: 100, properties: { '--rotate-y': '90deg', '--opacity': '0' } }
      ],
      duration: '600ms',
      timingFunction: TIMING_FUNCTIONS.easeIn,
      fillMode: 'both'
    }
  },
  
  zoomOut: {
    name: 'Zoom Out',
    description: 'Zoom exit animation',
    animation: {
      name: 'zoomOut',
      keyframes: [
        { offset: 0, properties: { '--scale': '1', '--opacity': '1' } },
        { offset: 50, properties: { '--opacity': '1' } },
        { offset: 100, properties: { '--scale': '0', '--opacity': '0' } }
      ],
      duration: '500ms',
      timingFunction: TIMING_FUNCTIONS.easeIn,
      fillMode: 'both'
    }
  }
};

/**
 * AnimationManager - Manages CSS animations and transitions
 */
export class AnimationManager {
  private animations: Map<string, Animation>;
  private transitions: Map<string, Transition[]>;

  constructor() {
    this.animations = new Map();
    this.transitions = new Map();
    
    // Load preset animations
    Object.values(ANIMATION_PRESETS).forEach(preset => {
      this.animations.set(preset.animation.name, preset.animation);
    });
  }

  /**
   * Create a transition
   */
  createTransition(
    property: string,
    duration: string,
    timingFunction: string = TIMING_FUNCTIONS.ease,
    delay?: string
  ): Transition {
    return {
      property,
      duration,
      timingFunction,
      delay
    };
  }

  /**
   * Create multiple transitions
   */
  createTransitions(transitions: Array<Omit<Transition, 'timingFunction'> & { timingFunction?: string }>): Transition[] {
    return transitions.map(t => ({
      property: t.property,
      duration: t.duration,
      timingFunction: t.timingFunction || TIMING_FUNCTIONS.ease,
      delay: t.delay
    }));
  }

  /**
   * Add transition to a style ID
   */
  addTransition(styleId: string, transition: Transition): void {
    const existing = this.transitions.get(styleId) || [];
    existing.push(transition);
    this.transitions.set(styleId, existing);
  }

  /**
   * Get transitions for a style
   */
  getTransitions(styleId: string): Transition[] {
    return this.transitions.get(styleId) || [];
  }

  /**
   * Generate CSS transition string
   */
  generateTransitionCSS(transitions: Transition[]): string {
    return transitions
      .map(t => {
        const parts = [t.property, t.duration, t.timingFunction];
        if (t.delay) parts.push(t.delay);
        return parts.join(' ');
      })
      .join(', ');
  }

  /**
   * Create a custom animation
   */
  createAnimation(animation: Animation): Animation {
    this.animations.set(animation.name, animation);
    return animation;
  }

  /**
   * Get animation by name
   */
  getAnimation(name: string): Animation | undefined {
    return this.animations.get(name);
  }

  /**
   * Get all animations
   */
  getAllAnimations(): Animation[] {
    return Array.from(this.animations.values());
  }

  /**
   * Generate CSS keyframes
   */
  generateKeyframesCSS(animation: Animation): string {
    const keyframeRules = animation.keyframes
      .map(kf => {
        const properties = Object.entries(kf.properties)
          .map(([key, value]) => `    ${key}: ${value};`)
          .join('\n');
        return `  ${kf.offset}% {\n${properties}\n  }`;
      })
      .join('\n');

    return `@keyframes ${animation.name} {\n${keyframeRules}\n}`;
  }

  /**
   * Generate CSS animation property
   */
  generateAnimationCSS(animation: Animation): string {
    const parts = [
      animation.name,
      animation.duration,
      animation.timingFunction
    ];
    
    if (animation.delay) parts.push(animation.delay);
    if (animation.iterationCount) parts.push(String(animation.iterationCount));
    if (animation.direction) parts.push(animation.direction);
    if (animation.fillMode) parts.push(animation.fillMode);
    
    return parts.join(' ');
  }

  /**
   * Generate complete CSS for an animation
   */
  generateCompleteAnimationCSS(animation: Animation): string {
    const keyframes = this.generateKeyframesCSS(animation);
    const animationProp = this.generateAnimationCSS(animation);
    
    return `${keyframes}\n\n.animated-${animation.name} {\n  animation: ${animationProp};\n}`;
  }

  /**
   * Get preset animation
   */
  getPreset(presetName: keyof typeof ANIMATION_PRESETS): Animation | undefined {
    const preset = ANIMATION_PRESETS[presetName];
    return preset?.animation;
  }

  /**
   * Get all preset names
   */
  getPresetNames(): string[] {
    return Object.keys(ANIMATION_PRESETS);
  }

  /**
   * Create animation variables for a style
   */
  createAnimationVariables(animation: Animation): Partial<StyleVariables> {
    const variables: Partial<StyleVariables> = {
      '--animation-name': animation.name,
      '--animation-duration': animation.duration,
      '--animation-timing-function': animation.timingFunction
    };
    
    if (animation.delay) {
      variables['--animation-delay'] = animation.delay;
    }
    if (animation.iterationCount) {
      variables['--animation-iteration-count'] = String(animation.iterationCount);
    }
    if (animation.direction) {
      variables['--animation-direction'] = animation.direction;
    }
    if (animation.fillMode) {
      variables['--animation-fill-mode'] = animation.fillMode;
    }
    
    return variables;
  }

  /**
   * Create transition variables for a style
   */
  createTransitionVariables(transitions: Transition[]): Partial<StyleVariables> {
    return {
      '--transition': this.generateTransitionCSS(transitions)
    };
  }

  /**
   * Combine multiple animations
   */
  combineAnimations(animations: Animation[]): string {
    return animations.map(a => this.generateAnimationCSS(a)).join(', ');
  }
}

/**
 * Singleton instance
 */
let animationManagerInstance: AnimationManager | null = null;

export function getAnimationManager(): AnimationManager {
  if (!animationManagerInstance) {
    animationManagerInstance = new AnimationManager();
  }
  return animationManagerInstance;
}

export function resetAnimationManager(): void {
  animationManagerInstance = null;
}
